import { type FC, useCallback } from 'react';
import { graphql, useFragment } from 'react-relay';
import { useDispatch } from 'react-redux';
import { useAuctionState, useAuctionPricing } from 'dibs-auctions/exports/hooks/index.ts';

// components
import { FormattedMessage } from 'dibs-react-intl';
import { PriceDisplay } from 'dibs-buyer-price';
import { PdpSharedAuctionSubscription } from '../PdpSharedAuctionSubscription/PdpSharedAuctionSubscription.tsx';

// helpers
import { updateRelayVariables } from '../../actions/sharedRelayVariableActions.js';

// styles
import styles from './PdpSharedCondensedAuctionPriceDisplay.scss';

/// types
import { type PdpSharedCondensedAuctionPriceDisplay$key } from './__generated__/PdpSharedCondensedAuctionPriceDisplay.graphql.ts';

type Props = {
    item: PdpSharedCondensedAuctionPriceDisplay$key;
    currency: string;
    isMultiSku: boolean;
    selectedSkuId: string | null;
};

export const PdpSharedCondensedPriceDisplay: FC<Props> = ({ item: itemRef, currency }) => {
    const item = useFragment(
        graphql`
            fragment PdpSharedCondensedAuctionPriceDisplay on Item {
                ...PdpSharedAuctionSubscription_item
                ...PriceDisplay_item
                    @arguments(
                        userId: $userId
                        isTrade: $isTrade
                        userCountryCode: $userCountryCode
                        priceBookName: $priceBookName
                        catchNonFatalErrors: false
                    )
                ...useAuctionState_item @arguments(page: pdp)
                ...useAuctionPricing_item @arguments(page: pdp)
            }
        `,
        itemRef
    );

    const dispatch = useDispatch();

    const { isPreviewAuction, isActiveAuction, isTransitionAuction } = useAuctionState({ item });
    const { isBuyItNowEligible } = useAuctionPricing({
        item,
        currency,
    });

    const refetch = useCallback(() => {
        dispatch(updateRelayVariables({ updateAuctionItem: true }));
    }, [dispatch]);

    let cta;

    if (isPreviewAuction || ((isActiveAuction || isTransitionAuction) && isBuyItNowEligible)) {
        cta = (
            <FormattedMessage
                id="PdpRespCondensedListedContent.startingBid"
                defaultMessage="Starting Bid"
            />
        );
    } else if (isActiveAuction || isTransitionAuction) {
        cta = (
            <FormattedMessage
                id="PdpRespCondensedListedContent.currentBid"
                defaultMessage="Current Bid"
            />
        );
    } else {
        cta = (
            <div className={styles.closed} data-tn="pdp-bidding-closed">
                <FormattedMessage
                    id="PdpRespCondensedListedContent.biddingClosed"
                    defaultMessage="Bidding Closed"
                />
            </div>
        );
    }

    return (
        <div className={styles.wrapper}>
            <PdpSharedAuctionSubscription refetch={refetch} item={item} />
            <div className={styles.cta}>{cta}</div>
            <PriceDisplay currency={currency} item={item} useSvgIcon sku={null} />
        </div>
    );
};
